import React from 'react';
import { SlidersHorizontal, X, LayoutGrid, Rows } from 'lucide-react';
import { PersonagemSortOption, PersonagemStatusFilter, PersonagemUsoFilter } from '../../../types/personagem';

interface PersonagensFilterBarProps {
  categorias: string[];
  statusFilter: PersonagemStatusFilter;
  categoriaFilter: string;
  usoFilter: PersonagemUsoFilter;
  precoMin: string;
  precoMax: string;
  sortOption: PersonagemSortOption;
  groupByCategory: boolean;
  resultCount: number;
  totalCount: number;
  onStatusChange: (value: PersonagemStatusFilter) => void;
  onCategoriaChange: (value: string) => void;
  onUsoChange: (value: PersonagemUsoFilter) => void;
  onPrecoMinChange: (value: string) => void;
  onPrecoMaxChange: (value: string) => void;
  onSortChange: (value: PersonagemSortOption) => void;
  onGroupByCategoryChange: (value: boolean) => void;
  onClearFilters: () => void;
}

const SORT_OPTIONS: Array<{ value: PersonagemSortOption; label: string }> = [
  { value: 'nome-asc', label: 'Nome (A-Z)' },
  { value: 'nome-desc', label: 'Nome (Z-A)' },
  { value: 'preco-desc', label: 'Preço base (maior primeiro)' },
  { value: 'preco-asc', label: 'Preço base (menor primeiro)' },
  { value: 'receita-desc', label: 'Valor arrecadado (maior primeiro)' },
  { value: 'receita-asc', label: 'Valor arrecadado (menor primeiro)' },
  { value: 'uso-desc', label: 'Mais usado em orçamentos' },
  { value: 'uso-asc', label: 'Menos usado em orçamentos' },
  { value: 'recente', label: 'Cadastro mais recente' },
  { value: 'antigo', label: 'Cadastro mais antigo' }
];

export const PersonagensFilterBar: React.FC<PersonagensFilterBarProps> = ({
  categorias,
  statusFilter,
  categoriaFilter,
  usoFilter,
  precoMin,
  precoMax,
  sortOption,
  groupByCategory,
  resultCount,
  totalCount,
  onStatusChange,
  onCategoriaChange,
  onUsoChange,
  onPrecoMinChange,
  onPrecoMaxChange,
  onSortChange,
  onGroupByCategoryChange,
  onClearFilters
}) => {
  const hasActiveFilters =
    statusFilter !== 'todos' ||
    categoriaFilter !== 'todas' ||
    usoFilter !== 'todos' ||
    precoMin !== '' ||
    precoMax !== '' ||
    sortOption !== 'nome-asc' ||
    groupByCategory;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <SlidersHorizontal className="w-4 h-4" />
          Filtros e ordenação
          <span className="text-xs font-normal text-slate-400">
            ({resultCount} de {totalCount})
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
            <button
              type="button"
              onClick={() => onGroupByCategoryChange(false)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !groupByCategory ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Lista única
            </button>
            <button
              type="button"
              onClick={() => onGroupByCategoryChange(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                groupByCategory ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Rows className="w-3.5 h-3.5" />
              Por categoria
            </button>
          </div>

          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value as PersonagemStatusFilter)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="todos">Todos</option>
            <option value="ativo">Somente ativos</option>
            <option value="inativo">Somente inativos</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Categoria</label>
          <select
            value={categoriaFilter}
            onChange={(e) => onCategoriaChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="todas">Todas</option>
            {categorias.map((categoria) => (
              <option key={categoria} value={categoria}>
                {categoria}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Uso em orçamentos</label>
          <select
            value={usoFilter}
            onChange={(e) => onUsoChange(e.target.value as PersonagemUsoFilter)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="todos">Todos</option>
            <option value="usados">Já usado</option>
            <option value="nao-usados">Nunca usado</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Preço base mín. (R$)</label>
          <input
            type="number"
            min={0}
            value={precoMin}
            onChange={(e) => onPrecoMinChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Ex: 100"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Preço base máx. (R$)</label>
          <input
            type="number"
            min={0}
            value={precoMax}
            onChange={(e) => onPrecoMaxChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Ex: 500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Ordenar por</label>
          <select
            value={sortOption}
            onChange={(e) => onSortChange(e.target.value as PersonagemSortOption)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
